import documentDuplicate from "@assets/images/document-duplicate.svg";
import pencilWhite from "@assets/images/pencil-white.svg";
import plusTealIcon from "@assets/images/plus-teal.svg";
import saveIcon from "@assets/images/save-icon-white.svg";
import { Button, IconButton, Paper, Tooltip } from "@material-ui/core";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { useUser } from "@saleor/auth";
import {
  ChangeOrderTab,
  ViewEditMode
} from "@saleor/changeOrderExport/views/ViewEditOrder/types";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import { menuWidth, shrunkMenuWidth } from "@saleor/components/Siderbar";
import { PermissionEnum } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { UseBulkActionsProps } from "@saleor/hooks/useBulkActions";
import { makeStyles } from "@saleor/macaw-ui";
import {
  formatThousandDecimal,
  shortenLongDecimal
} from "@saleor/utils/number";
import classNames from "classnames";
import React, { useMemo } from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

interface propsType {
  expandMenu?: boolean;
  bodyPaddingRight?: number;
}

const useStyles = makeStyles<propsType>(
  theme => ({
    menu: {
      padding: 0,
      position: "fixed",
      bottom: 0,
      right: 0,
      transition: "width 0.4s ease",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        padding: "0 2.4rem",
        paddingRight: props => `calc(2.4rem + ${props.bodyPaddingRight}px)`,
        width: props =>
          props.expandMenu
            ? `calc(100% - ${menuWidth}px)`
            : `calc(100% - ${shrunkMenuWidth}px)`
      },
      zIndex: 99
    },
    wrapper: {
      display: "block",
      width: "100%",
      alignItems: "center",
      boxShadow: "0px -8px 24px rgb(0 0 0 / 8%)",
      borderRadius: "20px 20px 0px 0px"
    },
    section: {
      display: "flex",
      padding: "1.6rem 2.4rem",
      justifyContent: "space-between",
      gap: "0 2.4rem"
    },
    divider: {
      margin: 0,
      border: 0,
      borderTop: `1px dashed ${theme.palette.grey[500]}`
    },
    summaryCaption: {
      color: theme.palette.grey[900]
    },
    number: {
      fontSize: "1.8rem",
      lineHeight: "2.4rem"
    },
    sectionRight: {
      display: "flex",
      gap: "1.6rem"
    },
    price: {
      minWidth: 152,
      marginRight: "2.4rem",
      "&:last-child": {
        marginRight: 0,
        minWidth: "75px"
      }
    },
    priceCaption: {
      color: theme.palette.grey[600],
      marginBottom: "0.6rem"
    },
    netPriceNumber: {
      color: theme.palette.info.dark
    },
    btnAddProduct: {
      alignItems: "center",
      height: 48,
      padding: "0 13px",
      "& .MuiButton-label": {
        fontWeight: 700,
        gap: "0 8px"
      }
    },
    btnEditOrder: {
      // color: "#ffffff",
      alignItems: "center",
      fontSize: "1.8rem",
      lineHeight: "2.8rem",
      height: 48,
      padding: "0 53px 0 54px",

      "& .MuiButton-label": {
        fontWeight: 700
      }
      // "& .plus-icon path": {
      //   stroke: theme.palette.info.main
      // }
    },
    btnConfirmOrder: {
      background:
        "linear-gradient(90deg, #601863 0%, #681661 12%, #81125C 32%, #A90C55 57%, #DF044A 86%, #FD0045 100%)",
      borderRadius: "4px",
      fontSize: "1.6rem",
      height: 48,
      padding: "0 25px",
      "& .MuiTypography-root": {
        color: "#FFFFFF"
      }
    },
    btnCancelItem: {
      color: "rgba(255, 38, 0, 1)",
      borderColor: "rgba(255, 38, 0, 1)",
      "&:hover": {
        borderColor: "rgba(255, 38, 0, 1)"
      },
      "&.MuiButton-outlined.Mui-disabled": {
        "& .MuiButton-label": {
          "& .MuiTypography-root": {
            color: "#CCC !important"
          }
        }
      },
      "& .MuiButton-label": {
        "& .MuiTypography-root": {
          color: "rgba(255, 38, 0, 1)"
        }
      },
      alignItems: "center",
      height: 48,
      padding: "12px 18px"
    },
    buttonContainedCommon: {
      padding: 12,
      width: 180,
      height: 48,
      fontSize: 16,
      lineHeight: "24px",
      "& .MuiButton-label": {
        fontWeight: 700
      }
    },
    btnChangeMode: {
      alignItems: "center",
      fontSize: "1.8rem",
      lineHeight: "2.8rem",
      height: 48,
      padding: "0 73px 0 74px",
      marginRight: 10,
      "& .MuiButton-label": {
        fontWeight: 700
      }
    },
    leftContainer: {
      display: "flex",
      alignItems: "center",
      "& button": {
        marginRight: "1.6rem",
        "&.Mui-disabled": {
          "& .MuiButton-label": {
            opacity: 0.3
          }
        }
      }
    },
    pagination: {
      display: "flex",
      justifyContent: "flex-end",
      color: theme.palette.grey[900],
      "& >div": {
        fontSize: 12,
        lineHeight: "18px"
      },
      "& .MuiSelect-root": {
        color: theme.palette.grey[900]
      },
      "& .MuiButtonBase-root": {
        borderRadius: "6px",
        width: "2.4rem",
        height: "2.4rem",
        boxShadow:
          "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.12)",
        border: "none",
        "&:hover": {
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.24)"
        },
        "&:active": {
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.24), 0px 0px 0px 3px rgba(1, 178, 237, 0.36)"
        },
        "& svg": {
          fontSize: "16px"
        }
      },
      "& .MuiButtonBase-root.Mui-disabled": {
        background: theme.palette.background.default,
        boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.08)",
        "& svg": {
          color: theme.palette.grey[500]
        }
      }
    },
    btnSubmit: {
      background:
        "linear-gradient(90deg, #601863 0%, #681661 12%, #81125C 32%, #A90C55 57%, #DF044A 86%, #FD0045 100%)",
      borderRadius: "4px",
      height: 48,
      fontSize: "1.6rem",
      padding: "0 25px",
      "& .MuiTypography-root": {
        color: "#FFFFFF"
      }
    },
    iconButton: {
      "& > span:first-of-type": {
        transition: "none"
      },
      padding: "0.4rem",
      "&:hover": {
        color: theme.palette.grey[900]
      }
    },
    inheritColor: {
      color: "inherit !important"
    },
    backButton: {
      color: "#ffffff",
      alignItems: "center",
      fontSize: "1.8rem",
      lineHeight: "2.8rem",
      height: 48,
      border: "1px solid #222222",
      padding: "0 53px 0 54px",
      "& .MuiTypography-root": {
        color: "#222222"
      }

      // }
    }
  }),
  { name: "CreateOrderFooter" }
);

interface CreateOrderFooterProps {
  onOrder: () => void;
  onPopup?: () => void;
  onNextTab?: (activeTab) => void;
  onPreviousTab?: (activeTab) => void;
  onDuplicate?: () => void;
  activeTab: ChangeOrderTab;
  disabled?: boolean;
  disabledUpdate?: boolean;
  disabledAddMore?: boolean;
  hasValidationError?: boolean;
  priceData: {
    itemCount: number;
    totalPrice: string;
    vat: string;
    netPrice: string;
    currency: string;
  };
  viewEditMode: ViewEditMode;
  bulkActions: UseBulkActionsProps;
  onOpenAtpCtp: () => void;
  onChangeMode: () => void;
  handleCancelEdit: () => void;
  taxPercent: number;
  onCancelItem: (listItemNo) => void;
  setOpenCancelAddNewItem?: (isOpen) => void;
  setOpenConfirmAddNewItem: (isOpen) => void;
  orderStatus: string;
  isFlagR5?: boolean;
}

const ChangeOrderFooter: React.FC<CreateOrderFooterProps> = ({
  onOrder,
  onPopup,
  onNextTab,
  onPreviousTab,
  onDuplicate,
  onChangeMode,
  activeTab,
  disabled,
  disabledUpdate,
  hasValidationError,
  priceData,
  disabledAddMore,
  viewEditMode,
  onOpenAtpCtp,
  bulkActions,
  taxPercent,
  onCancelItem,
  setOpenCancelAddNewItem,
  setOpenConfirmAddNewItem,
  handleCancelEdit,
  orderStatus,
  isFlagR5
}) => {
  const [appState, _] = useAppState();
  const { user } = useUser();

  const isChangeOrderExport = useMemo(
    () =>
      !!user.userPermissions.find(
        e => e.code === PermissionEnum.CHANGE_ORDER_EXPORT
      ),
    [user.userPermissions]
  );

  const classes = useStyles({
    expandMenu: appState.expandMenu,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();
  const { itemCount, totalPrice, vat, netPrice } = priceData;
  const handleNextTab = () => {
    switch (activeTab) {
      case ChangeOrderTab.agency:
        onNextTab(ChangeOrderTab.orderHeader);
        break;
      case ChangeOrderTab.orderHeader:
        onNextTab(ChangeOrderTab.materialItem);
        break;
      default:
        break;
    }
  };
  const handlePrevTab = () => {
    switch (activeTab) {
      case ChangeOrderTab.orderHeader:
        onPreviousTab(ChangeOrderTab.agency);
        break;
      case ChangeOrderTab.materialItem:
        onPreviousTab(ChangeOrderTab.orderHeader);
        break;
      default:
        break;
    }
  };

  const { listElements } = bulkActions;

  const ButtonAddItem = () => (
    <Button
      variant="outlined"
      color="secondary"
      onClick={onPopup}
      className={classes.btnAddProduct}
      disabled={
        !isChangeOrderExport ||
        orderStatus === "Completed Delivery" ||
        orderStatus === "Complete Order" ||
        orderStatus === "Completed Order" ||
        isFlagR5
      }
      startIcon={<InlineSVG stroke="currentColor" src={plusTealIcon} />}
      name="add-product-button"
      id="add-product-button"
      data-testid="add-product-button"
    >
      <MyTypography variant={"button2"} className={classes.inheritColor}>
        {intl.formatMessage({
          defaultMessage: "เพิ่มรายการสินค้า"
        })}
      </MyTypography>
    </Button>
  );

  const ButtonSubmitOrder = () => (
    <Button
      variant={"contained"}
      color={"secondary"}
      onClick={() => setOpenConfirmAddNewItem(true)}
      // disabled={!!splitLineId}
      className={classes.btnSubmit}
      startIcon={<InlineSVG src={saveIcon} />}
      name="submit-order-button"
      id="submit-order-button"
    >
      <MyTypography variant={"body2"}>
        <b>
          {intl.formatMessage({
            defaultMessage: "บันทึกคำสั่งซื้อ"
          })}
        </b>
      </MyTypography>
    </Button>
  );

  return (
    <div className={classes.menu} data-testid="changeOrderFooter">
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "Order Summary"
              })}
            </MyTypography>
            <MyTypography variant="body1" className={classes.number}>
              ({itemCount}{" "}
              {itemCount >= 2
                ? intl.formatMessage({ defaultMessage: "items" })
                : intl.formatMessage({ defaultMessage: "item" })}
              )
            </MyTypography>
          </div>
          <div className={classes.sectionRight}>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมสุทธิ"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {netPrice ? (
                  <DecimalFormat
                    value={+netPrice}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {`ภาษีมูลค่าเพิ่ม ${shortenLongDecimal(taxPercent * 100)}%`}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {vat ? (
                  <DecimalFormat
                    value={+vat}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม"
                })}
              </MyTypography>
              <MyTypography
                variant="subtitle1"
                className={classes.netPriceNumber}
              >
                <span className={classes.totalPrice}>
                  {Number(totalPrice)
                    ? formatThousandDecimal(+totalPrice, 2)
                    : "0.00"}
                </span>
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "Currency"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {priceData.currency}
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.section}>
          <div className={classes.leftContainer}>
            {viewEditMode !== ViewEditMode.ADD ? (
              <>
                <div className={classes.pagination}>
                  <IconButton
                    className={classNames(classes.iconButton)}
                    onClick={handlePrevTab}
                    disabled={activeTab === ChangeOrderTab.agency}
                    name="previous-tab-button"
                    id="previous-tab-button"
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                  <IconButton
                    className={classNames(classes.iconButton)}
                    onClick={handleNextTab}
                    disabled={activeTab === ChangeOrderTab.materialItem}
                    name="next-tab-button"
                    id="next-tab-button"
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </div>
                {/* {activeTab === ChangeOrderTab.materialItem &&
            viewEditMode === ViewEditMode.EDIT ? (
              <Button
                variant="outlined"
                onClick={onCancelItem}
                className={classes.btnCancelItem}
                disabled={!listElements.length}
              >
                <MyTypography variant={"body2"}>
                  {intl.formatMessage({ defaultMessage: "Cancel item" })} (
                  <b>{listElements.length}</b>)
                </MyTypography>
              </Button>
            ) : null} */}
                {activeTab === ChangeOrderTab.materialItem &&
                viewEditMode === ViewEditMode.EDIT ? (
                  <Button
                    variant="outlined"
                    onClick={onCancelItem}
                    className={classes.btnCancelItem}
                    disabled={
                      !listElements.length || disabledAddMore || isFlagR5
                    }
                    id="cancel-item-button"
                    name="cancel-item-button"
                    data-testid="cancel-item-button"
                  >
                    <MyTypography variant={"body2"}>
                      {intl.formatMessage({ defaultMessage: "Cancel item" })} (
                      <b>{listElements.length}</b>)
                    </MyTypography>
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onOpenAtpCtp}
                  disabled={
                    !isChangeOrderExport || !listElements.length || isFlagR5
                  }
                  className={classes.btnAddProduct}
                  name="atp-ctp-button"
                  id="atp-ctp-button"
                  data-testid="atp-ctp-button"
                >
                  <span>
                    <FormattedMessage defaultMessage="ATP/CTP" />
                  </span>
                </Button>
                <Tooltip title={"Copy order"}>
                  <div>
                    <Button
                      variant={"outlined"}
                      color={"secondary"}
                      onClick={onDuplicate}
                      disabled={!isChangeOrderExport}
                      name="copy-order-button"
                      id="copy-order-button"
                      data-testid="copy-order-button"
                    >
                      <img
                        src={documentDuplicate}
                        alt="document-duplicate-icon"
                      />
                    </Button>
                  </div>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="default"
                  className={classes.backButton}
                  onClick={() => setOpenCancelAddNewItem(true)}
                  // onClick={onOpenAtpCtp}
                  // disabled={!listElements.length}
                  // className={classes.btnAddProduct}
                  name="cancel-add-new-item-button"
                  id="cancel-add-new-item-button"
                >
                  <MyTypography variant="button2">
                    <FormattedMessage defaultMessage="ยกเลิกเพิ่มรายการ" />
                  </MyTypography>
                </Button>
              </>
            )}
          </div>
          <div className={classes.sectionRight}>
            {viewEditMode === ViewEditMode.VIEW ? (
              <>
                <ButtonAddItem />
                <Button
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={onChangeMode}
                  disabled={
                    !isChangeOrderExport ||
                    (activeTab !== ChangeOrderTab.orderHeader &&
                      (orderStatus === "Completed Delivery" ||
                        orderStatus === "Complete Order" ||
                        orderStatus === "Completed Order"))
                  }
                  className={classes.btnEditOrder}
                  startIcon={
                    <InlineSVG
                      stroke="currentColor"
                      className="plus-icon"
                      src={pencilWhite}
                    />
                  }
                  name="edit-order-button"
                  id="edit-order-button"
                  data-testid="edit-order-button"
                >
                  <MyTypography
                    className={classes.inheritColor}
                    variant={"body2"}
                  >
                    <b>
                      {intl.formatMessage({
                        defaultMessage: "แก้ไขคำสั่งซื้อ"
                      })}
                    </b>
                  </MyTypography>
                </Button>
              </>
            ) : null}
            {viewEditMode === ViewEditMode.EDIT ? (
              <>
                <Button
                  variant={"outlined"}
                  className={classNames(
                    classes.buttonContainedCommon,
                    "defaultB"
                  )}
                  color={"secondary"}
                  onClick={handleCancelEdit}
                  name="cancel-edit-button"
                  id="cancel-edit-button"
                >
                  <MyTypography variant={"body2"}>
                    {intl.formatMessage({ defaultMessage: "ยกเลิกแก้ไข" })}
                  </MyTypography>
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onOrder}
                  disabled={hasValidationError || disabled || disabledUpdate}
                  startIcon={<InlineSVG src={saveIcon} />}
                  className={classes.btnConfirmOrder}
                  name="submit-order-button"
                  id="submit-order-button"
                >
                  <FormattedMessage defaultMessage="บันทึกคำสั่งซื้อ" />
                </Button>
              </>
            ) : (
              ""
            )}
            {viewEditMode === ViewEditMode.ADD ? (
              <>
                <ButtonAddItem />
                <ButtonSubmitOrder />
              </>
            ) : (
              ""
            )}
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default ChangeOrderFooter;
